import { useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { Icon } from "../../ui/Icon";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../../ui/Toast";
import { SearchSelect, type SSOption } from "../../ui/SearchSelect";
import { Drawer } from "../../Drawer";
import { DocChip } from "../../shipment/DocChip";
import { EmployeeBulkImportDrawer } from "../EmployeeBulkImportDrawer";
import { FilePreviewModal } from "../../shipment/FilePreviewModal";
import { parseTrNumber } from "../../../lib/trNumber";
import { useIsMobile } from "../../../lib/useIsMobile";
import { MCard } from "../../../components/mobile/primitives";
import "../../../pages/Items.css";
import "../../../pages/Maintenance.css";
import "./EmployeesTab.css";

/**
 * STEPERP — İK / Personeller sekmesi (Faz 3-4 frontend).
 *
 * En kapsamlı İK sekmesi: arama + durum filtresi + liste tablosu; satır → detay Drawer (lg).
 * Detay üç bölüm: (a) Özlük + görev geçmişi, (b) Evrak (SKT renkli + yükle/önizle/sil),
 * (c) Maaş (YALNIZ süper-admin). Yazma aksiyonları yalnız `canManage`; maaş bölümü yalnız `isSuper`.
 * Kendine yeterli: kendi query/mutation/state. Tasarım sistemi KATI (btn-md, dt2, badge-pill, form-*).
 *
 * NOT: Personel-kullanıcı bağı (userId) formda yok — `user.list` `manage:user` ister; İK kullanıcısı
 * bu izne sahip olmayabilir, bu yüzden bilinçli olarak atlandı (kırık/FORBIDDEN sorgu doğmasın).
 */

type Status = "active" | "on_leave" | "terminated" | "candidate";
type Gender = "male" | "female" | "other" | "unspecified";
type Marital = "single" | "married" | "divorced" | "widowed" | "unspecified";
type EmpType = "full_time" | "part_time" | "contractor" | "intern" | "temporary";
type DocCategory =
  | "contract" | "id_card" | "diploma" | "certificate" | "health_report"
  | "criminal_record" | "photo" | "payroll_doc" | "performance" | "other";
type Currency = "TRY" | "USD" | "EUR" | "GBP";
type SalaryType = "base" | "bonus" | "allowance" | "deduction" | "overtime_rate";

/** Durum = renk + ikon + metin (renk tek başına anlatmaz). */
const STATUS_META: Record<Status, { cls: string; label: string; icon: string }> = {
  active: { cls: "b-success", label: "Aktif", icon: "check" },
  on_leave: { cls: "b-warn", label: "İzinli", icon: "clock" },
  candidate: { cls: "b-info", label: "Aday", icon: "user" },
  terminated: { cls: "", label: "Ayrıldı", icon: "x" },
};
const STATUS_ORDER: Status[] = ["active", "on_leave", "candidate", "terminated"];

const GENDER_LABEL: Record<Gender, string> = {
  male: "Erkek", female: "Kadın", other: "Diğer", unspecified: "Belirtilmemiş",
};
const MARITAL_LABEL: Record<Marital, string> = {
  single: "Bekâr", married: "Evli", divorced: "Boşanmış", widowed: "Dul", unspecified: "Belirtilmemiş",
};
const EMP_TYPE_LABEL: Record<EmpType, string> = {
  full_time: "Tam zamanlı", part_time: "Yarı zamanlı", contractor: "Sözleşmeli",
  intern: "Stajyer", temporary: "Geçici",
};
const DOC_CATEGORY_LABEL: Record<DocCategory, string> = {
  contract: "Sözleşme", id_card: "Kimlik", diploma: "Diploma", certificate: "Sertifika",
  health_report: "Sağlık Raporu", criminal_record: "Adli Sicil", photo: "Fotoğraf",
  payroll_doc: "Bordro", performance: "Performans", other: "Diğer",
};
const SALARY_TYPE_LABEL: Record<SalaryType, string> = {
  base: "Temel Ücret", bonus: "Prim / Bonus", allowance: "Ek Ödeme", deduction: "Kesinti",
  // AYLIK değil SAATLİK: bu kişiye özel istisna, pozisyonun mesai saat ücretini ezer.
  overtime_rate: "Mesai Saat Ücreti",
};

// ── Tarih / sayı yardımcıları (Date alanları transformer olmadığından string gelir) ──
function fmtDate(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
}
function toDateInput(v: string | Date | null | undefined): string {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}
function fmtMoney(n: number): string {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}
/** Evrak SKT rozeti: geçmiş → kırmızı, 30 gün içinde → turuncu, aksi → nötr. */
function expiryBadge(expiresAt: string | Date | null | undefined): { cls: string; label: string } | null {
  if (!expiresAt) return null;
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  if (days < 0) return { cls: "b-danger", label: `SKT geçti · ${fmtDate(expiresAt)}` };
  if (days <= 30) return { cls: "b-warn", label: `SKT ${days} gün · ${fmtDate(expiresAt)}` };
  return { cls: "", label: `SKT ${fmtDate(expiresAt)}` };
}

// ════════════════════════════════════════════════════════════════════════════

export function EmployeesTab({ canManage, isSuper }: { canManage: boolean; isSuper: boolean }) {
  const qc = trpc.useUtils();
  const isMobile = useIsMobile();

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) =>
    setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState<Status | "">("");
  const [positionF, setPositionF] = useState<string | null>(null);
  const [orgUnitF, setOrgUnitF] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ id: string | null } | null>(null); // id null → yeni personel
  const [importOpen, setImportOpen] = useState(false);

  const listQ = trpc.hr.employee.list.useQuery(
    {
      status: statusF || undefined,
      q: q.trim() || undefined,
      positionId: positionF ?? undefined,
      orgUnitId: orgUnitF ?? undefined,
    },
    { staleTime: 5_000 },
  );
  const rows = listQ.data ?? [];

  // Filtre seçenekleri. orgUnit.list `view:org` ister — İK kullanıcısında olmayabilir →
  // retry:false ve yetki yoksa Birim filtresi HİÇ render edilmez (boş/kırık filtre gösterme).
  const fPositionsQ = trpc.hr.position.list.useQuery(undefined, { staleTime: 60_000 });
  const fOrgUnitsQ = trpc.hr.orgUnit.list.useQuery(undefined, { staleTime: 60_000, retry: false });
  const fStatusOptions = useMemo<SSOption[]>(
    () => STATUS_ORDER.map((s) => ({ value: s, label: STATUS_META[s].label, icon: STATUS_META[s].icon })), []);
  const fPositionOptions = useMemo<SSOption[]>(
    () => (fPositionsQ.data ?? []).map((p) => ({ value: p.id, label: p.title, tail: p.code ?? undefined, icon: "user" })),
    [fPositionsQ.data]);
  const fOrgUnitOptions = useMemo<SSOption[]>(
    () => (fOrgUnitsQ.data ?? []).map((u) => ({ value: u.id, label: u.name, icon: "building" })),
    [fOrgUnitsQ.data]);
  const filtersActive = !!(q || statusF || positionF || orgUnitF);
  const clearFilters = () => { setQ(""); setStatusF(""); setPositionF(null); setOrgUnitF(null); };

  // ── Toplu seçim ──
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false); // yalnız mobil
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Filtre değişince seçim TEMİZLENİR: aksi halde gizlenen satırlar seçili kalır ve toplu
  // işlem kullanıcının GÖRMEDİĞİ personelleri değiştirir/siler.
  useEffect(() => { setSelectedIds(new Set()); }, [q, statusF, positionF, orgUnitF]);

  // Liste her tazelendiğinde seçim GÖRÜNEN satırlara budanır. Kritik: liste 5 sn'de bir
  // tazelenir; bu arada başka bir kullanıcı kayıt silerse/durum değiştirirse seçili id'ler
  // ekranda olmayan satırları işaret ederdi ve toplu silme onları da götürürdü.
  const dataStamp = listQ.dataUpdatedAt;
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(rows.map((r) => r.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataStamp]);

  const allShownSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someShownSelected = selectedIds.size > 0 && !allShownSelected;
  // Sunucu tek çağrıda en fazla 100 id kabul ediyor → "tümünü seç" de aynı sınırda durur,
  // aksi halde 250 kişilik tesiste buton aktif görünür ve ham zod hatasına düşerdi.
  const BULK_MAX = 100;
  const overBulkLimit = selectedIds.size > BULK_MAX;

  const bulkUpdMut = trpc.hr.employee.bulkUpdate.useMutation({
    onSuccess: (r) => {
      qc.hr.employee.invalidate();
      setSelectedIds(new Set());
      setBulkOpen(false);
      pushToast(r.skipped.length === 0
        ? { tone: "success", message: `${r.updated} personel güncellendi.` }
        : { tone: "warning", title: `${r.updated} güncellendi, ${r.skipped.length} atlandı`,
            lines: r.skipped.slice(0, 5).map((s) => `${s.code ?? s.id}: ${s.reason}`) });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Toplu işlem başarısız", message: e.message }),
  });
  const bulkDelMut = trpc.hr.employee.bulkRemove.useMutation({
    onSuccess: (r) => {
      qc.hr.employee.invalidate();
      setSelectedIds(new Set());
      setBulkDelOpen(false);
      pushToast(r.skipped.length === 0
        ? { tone: "success", message: `${r.deleted} personel silindi.` }
        : { tone: "warning", title: `${r.deleted} silindi, ${r.skipped.length} atlandı`,
            lines: r.skipped.slice(0, 5).map((s) => `${s.code ?? s.id}: ${s.reason}`) });
    },
    onError: (e) => { setBulkDelOpen(false); pushToast({ tone: "danger", title: "Toplu silme başarısız", message: e.message }); },
  });

  const drawers = (
    <>
      {detail ? (
        <EmployeeDetail
          key={detail.id ?? "new"}
          employeeId={detail.id}
          canManage={canManage}
          isSuper={isSuper}
          onClose={() => setDetail(null)}
          onCreated={(id) => setDetail({ id })}
          onChanged={() => qc.hr.employee.invalidate()}
          pushToast={pushToast}
        />
      ) : null}

      {canManage ? (
        <EmployeeBulkImportDrawer
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={() => qc.hr.employee.invalidate()}
        />
      ) : null}

      {canManage && bulkOpen ? (
        <BulkChangeDrawer
          count={selectedIds.size}
          positionOptions={fPositionOptions}
          orgUnitOptions={fOrgUnitOptions}
          busy={bulkUpdMut.isPending}
          onClose={() => setBulkOpen(false)}
          onApply={(payload) => bulkUpdMut.mutate({ ids: [...selectedIds], ...payload })}
        />
      ) : null}

      <ConfirmDialog
        open={bulkDelOpen}
        title={`${selectedIds.size} personel kalıcı olarak silinsin mi?`}
        message={
          <>
            Seçilen <b className="mono">{selectedIds.size}</b> personel ve bağlı tüm kayıtları
            (izin, puantaj, maaş, bordro, evrak dosyaları, görev geçmişi, performans) <b>kalıcı olarak</b> silinecek;
            bu işlem geri alınamaz. Onaylanmış/ödenmiş bordroda kalemi olanlar ve kendisine bağlı personeli
            bulunanlar atlanır. Geçmişi korumak için genelde durumu <b>"Ayrıldı"</b> yapmak tercih edilir.
          </>
        }
        tone="danger"
        confirmLabel="Kalıcı Olarak Sil"
        cancelLabel="Vazgeç"
        busy={bulkDelMut.isPending}
        onCancel={() => setBulkDelOpen(false)}
        onConfirm={() => bulkDelMut.mutate({ ids: [...selectedIds] })}
      />

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );

  if (isMobile) {
    const mb = (c: string) => (c === "b-warn" ? "b-warning" : c || "b-neutral");
    return (
      <>
        <div className="m-stickytop">
          {canManage ? (
            <div className="m-actions">
              <button
                type="button"
                className="btn-md primary sm"
                onClick={() => setDetail({ id: null })}
              >
                <Icon name="plus" size={14} />Personel Ekle
              </button>
              <button
                type="button"
                className="btn-md secondary sm"
                onClick={() => setImportOpen(true)}
              >
                <Icon name="upload" size={14} />Excel
              </button>
              <button
                type="button"
                className={`btn-md secondary sm${selectMode ? " act" : ""}`}
                aria-pressed={selectMode}
                onClick={() => { setSelectMode((v) => !v); setSelectedIds(new Set()); }}
              >
                <Icon name="checkCircle" size={14} />{selectMode ? "Bitir" : "Seç"}
              </button>
            </div>
          ) : null}
          <div className="m-search">
            <Icon name="search" size={15} />
            <input
              type="search"
              placeholder="Ad, soyad veya personel kodu…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Personel ara"
            />
          </div>
          <div className="m-chips">
            {([["", "Tümü"], ...STATUS_ORDER.map((s) => [s, STATUS_META[s].label] as [string, string])] as Array<[string, string]>).map(([v, lb]) => (
              <button key={lb} type="button" className={`m-chip${statusF === v ? " act" : ""}`} onClick={() => setStatusF(v as Status | "")}>{lb}</button>
            ))}
          </div>
        </div>

        {listQ.isError ? (
          <div className="m-empty">Personeller yüklenemedi: {listQ.error.message}</div>
        ) : listQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">
            {filtersActive
              ? "Filtreye uygun personel yok — aramayı veya durum filtresini değiştirin."
              : canManage ? '"Personel Ekle" ile ilk personel kartını oluşturun.' : "Görüntülenecek personel yok."}
          </div>
        ) : (
          rows.map((r) => {
            const m = STATUS_META[r.status as Status] ?? STATUS_META.active;
            return (
              <MCard
                pad key={r.id}
                className={`emp-mcard${selectMode && selectedIds.has(r.id) ? " sel" : ""}`}
                onClick={() => (selectMode ? toggleSelect(r.id) : setDetail({ id: r.id }))}
              >
                <div className="emp-mc-top">
                  {selectMode ? (
                    <span className="m-cardcheck" aria-hidden="true">
                      {selectedIds.has(r.id) ? <Icon name="check" size={14} /> : null}
                    </span>
                  ) : null}
                  <span className="emp-mc-id">
                    <span className="nm">{r.fullName}</span>
                    <span className="mono cd">{r.code}</span>
                  </span>
                  <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                </div>
                <div className="emp-mc-meta">
                  <div>
                    <div className="m-ovl">Pozisyon</div>
                    <div className="v">{r.positionTitle ?? "—"}</div>
                  </div>
                  <div>
                    <div className="m-ovl">Birim</div>
                    <div className="v">{r.orgUnitName ?? "—"}</div>
                  </div>
                  <div>
                    <div className="m-ovl">İşe Giriş</div>
                    <div className="mono v">{fmtDate(r.hireDate)}</div>
                  </div>
                </div>
              </MCard>
            );
          })
        )}

        {selectMode && selectedIds.size > 0 ? (
          <div className="m-selbar">
            <span className="cnt">{selectedIds.size} seçili</span>
            <span className="sp" />
            <button type="button" className="btn-md secondary sm" onClick={() => setSelectedIds(new Set())}>Temizle</button>
            <button type="button" className="btn-md primary sm" onClick={() => setBulkOpen(true)}>
              <Icon name="edit" size={14} />Değiştir
            </button>
          </div>
        ) : null}

        {drawers}
      </>
    );
  }

  return (
    <div>
      <div className="dt2-wrap">
        {/* Filtre çubuğu: `.dt2-toolbar` + sabit genişlikli `.dt2-filter` hücreleri (uygulamanın
            baskın deseni). `.mt-filter`'ın `flex: 1 1 170px`'i alanları kalan tüm genişliğe
            yayıyordu — tek "Durum" select'i ~700px oluyordu. */}
        <div className="dt2-toolbar hr-toolbar">
          <div className="dt2-search-box">
            <Icon name="search" size={13} />
            <input
              type="search"
              placeholder="Ad, soyad veya personel kodu…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Personel ara"
            />
          </div>

          <div className="dt2-filter sm">
            <SearchSelect
              value={statusF || null}
              options={fStatusOptions}
              onChange={(v) => setStatusF((v as Status) ?? "")}
              placeholder="Tüm durumlar"
              showFooter={false}
            />
          </div>

          <div className="dt2-filter lg">
            <SearchSelect
              value={positionF}
              options={fPositionOptions}
              onChange={setPositionF}
              placeholder="Tüm pozisyonlar"
              searchPlaceholder="Pozisyon ara…"
              emptyText="Pozisyon yok"
              icon="user"
            />
          </div>

          {/* Birim filtresi yalnız `view:org` yetkisi VARSA (yoksa liste boş döner). */}
          {fOrgUnitOptions.length > 0 ? (
            <div className="dt2-filter lg">
              <SearchSelect
                value={orgUnitF}
                options={fOrgUnitOptions}
                onChange={setOrgUnitF}
                placeholder="Tüm birimler"
                searchPlaceholder="Birim ara…"
                emptyText="Birim yok"
                icon="building"
              />
            </div>
          ) : null}

          {filtersActive ? (
            <button type="button" className="btn-md ghost" onClick={clearFilters} title="Filtreleri temizle">
              <Icon name="x" size={14} />Temizle
            </button>
          ) : null}

          <span className="grow" />

          {canManage ? (
            <>
              <button type="button" className="btn-md secondary" onClick={() => setImportOpen(true)}>
                <Icon name="upload" size={14} />Excel ile İçe Aktar
              </button>
              <button type="button" className="btn-md primary" onClick={() => setDetail({ id: null })}>
                <Icon name="plus" size={14} />Personel Ekle
              </button>
            </>
          ) : null}
        </div>

        {canManage && selectedIds.size > 0 ? (
          <div className="hr-bulk" role="status" aria-live="polite">
            <span className="hr-bulk-n"><b className="mono">{selectedIds.size}</b> personel seçili</span>
            {overBulkLimit ? (
              <span className="hr-bulk-warn"><Icon name="alertTriangle" size={12} />Tek seferde en fazla {BULK_MAX} personel işlenebilir</span>
            ) : null}
            <button type="button" className="btn-md secondary" onClick={() => setBulkOpen(true)} disabled={overBulkLimit || bulkUpdMut.isPending || bulkDelMut.isPending}>
              <Icon name="edit" size={14} />Toplu Değiştir
            </button>
            {isSuper ? (
              <button type="button" className="btn-md danger" onClick={() => setBulkDelOpen(true)} disabled={overBulkLimit || bulkUpdMut.isPending || bulkDelMut.isPending}>
                <Icon name="trash" size={14} />Kalıcı Sil
              </button>
            ) : null}
            <button type="button" className="hr-bulk-clear" onClick={() => setSelectedIds(new Set())}>
              <Icon name="x" size={12} />Seçimi temizle
            </button>
            {bulkUpdMut.isPending || bulkDelMut.isPending ? <span className="hr-bulk-busy">Uygulanıyor…</span> : null}
          </div>
        ) : null}

        <div className="dt2-scroll">
          {listQ.isError ? (
            <div className="form-banner-err emp-listerr">Personeller yüklenemedi: {listQ.error.message}</div>
          ) : listQ.isLoading ? (
            <div className="mt-empty">Yükleniyor…</div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <span className="ic" aria-hidden="true"><Icon name="users" size={30} /></span>
              <p className="ttl">Personel bulunamadı</p>
              <p className="sub">
                {filtersActive
                  ? "Filtreye uygun personel yok — arama, durum, pozisyon veya birim filtresini değiştirin."
                  : canManage ? "\"Personel Ekle\" ile ilk personel kartını oluşturun." : "Görüntülenecek personel yok."}
              </p>
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  {canManage ? (
                    <th className="hr-chk-col">
                      <input
                        type="checkbox"
                        aria-label={allShownSelected ? "Seçimi temizle" : `Görünen personelleri seç (${Math.min(rows.length, BULK_MAX)})`}
                        checked={allShownSelected}
                        // "kısmi seçim" (indeterminate) React prop'u yok → DOM üzerinden.
                        ref={(el) => { if (el) el.indeterminate = someShownSelected; }}
                        onChange={(e) => setSelectedIds(e.target.checked ? new Set(rows.slice(0, BULK_MAX).map((r) => r.id)) : new Set())}
                      />
                    </th>
                  ) : null}
                  <th className="emp-w-code">Kod</th>
                  <th>Ad Soyad</th>
                  <th>Pozisyon</th>
                  <th>Birim</th>
                  <th className="emp-w-date">İşe Giriş</th>
                  <th className="emp-w-date">Durum</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const m = STATUS_META[r.status as Status] ?? STATUS_META.active;
                  return (
                    <tr key={r.id} className="emp-row" onClick={() => setDetail({ id: r.id })} title="Personel kartını aç">
                      {canManage ? (
                        // Yayılımı <td> KESER, <input> değil: input'ta durdurulsaydı hücrenin
                        // boşluğuna tıklamak yine drawer'ı açardı.
                        <td className="hr-chk-col" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            aria-label={`${r.fullName} seç`}
                            checked={selectedIds.has(r.id)}
                            onChange={() => toggleSelect(r.id)}
                          />
                        </td>
                      ) : null}
                      <td className="cell-id">{r.code}</td>
                      <td className="emp-name">{r.fullName}</td>
                      <td>{r.positionTitle ?? <span className="emp-muted">—</span>}</td>
                      <td>{r.orgUnitName ?? <span className="emp-muted">—</span>}</td>
                      <td className="mono emp-date">{fmtDate(r.hireDate)}</td>
                      <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="dt2-foot"><span>{rows.length} personel</span></div>
      </div>

      {drawers}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Toplu Değiştirme Drawer'ı — yalnız ORTAK alanlar (kişiye özgü veri toplu yazılmaz)
// ════════════════════════════════════════════════════════════════════════════

type BulkPayload = {
  status?: Status;
  terminationDate?: string | null;
  positionId?: string | null;
  orgUnitId?: string | null;
  employmentType?: EmpType;
};

/** "Değiştirme" (dokunma) ile "temizle" (null yaz) ayrı seçeneklerdir — ikisi karıştırılırsa veri silinir. */
const KEEP = "__keep__";
const CLEAR = "__clear__";

function BulkChangeDrawer({
  count, positionOptions, orgUnitOptions, busy, onClose, onApply,
}: {
  count: number;
  positionOptions: SSOption[];
  orgUnitOptions: SSOption[];
  busy: boolean;
  onClose: () => void;
  onApply: (payload: BulkPayload) => void;
}) {
  const [status, setStatus] = useState<Status | "">("");
  const [terminationDate, setTerminationDate] = useState("");
  const [position, setPosition] = useState<string>(KEEP);
  const [orgUnit, setOrgUnit] = useState<string>(KEEP);
  const [empType, setEmpType] = useState<EmpType | "">("");

  const posOpts = useMemo<SSOption[]>(
    () => [{ value: KEEP, label: "Değiştirme" }, { value: CLEAR, label: "Temizle (boş bırak)" }, ...positionOptions],
    [positionOptions]);
  const unitOpts = useMemo<SSOption[]>(
    () => [{ value: KEEP, label: "Değiştirme" }, { value: CLEAR, label: "Temizle (boş bırak)" }, ...orgUnitOptions],
    [orgUnitOptions]);

  const payload = (): BulkPayload => ({
    ...(status ? { status } : {}),
    ...(status === "terminated" && terminationDate ? { terminationDate } : {}),
    ...(position !== KEEP ? { positionId: position === CLEAR ? null : position } : {}),
    ...(orgUnit !== KEEP ? { orgUnitId: orgUnit === CLEAR ? null : orgUnit } : {}),
    ...(empType ? { employmentType: empType } : {}),
  });
  const hasChange = Object.keys(payload()).some((k) => k !== "terminationDate");

  return (
    <Drawer
      open
      onClose={onClose}
      title="Seçili personelleri toplu değiştir"
      subtitle={`${count} personel seçili`}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={busy}>Vazgeç</button>
          <button type="button" className="btn-md primary" disabled={!hasChange || busy} onClick={() => onApply(payload())}>
            {busy ? "Uygulanıyor…" : `Uygula (${count})`}
          </button>
        </>
      }
    >
      <div className="form-banner-info">
        <Icon name="info" size={14} className="hr-bnr-ic" />
        <div>
          Yalnız <b>seçtiğiniz alanlar</b> değişir; "Değiştirme" bırakılan alanlara dokunulmaz.
          Ad, TC, IBAN gibi kişiye özgü bilgiler toplu değiştirilemez.
        </div>
      </div>

      <div className="form-field">
        <label className="lbl" htmlFor="bulk-status">Durum</label>
        <select id="bulk-status" className="form-select" value={status} onChange={(e) => setStatus(e.target.value as Status | "")} disabled={busy}>
          <option value="">Değiştirme</option>
          {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
      </div>

      {status === "terminated" ? (
        <div className="form-field">
          <label className="lbl" htmlFor="bulk-term">Çıkış Tarihi</label>
          <input id="bulk-term" type="date" className="form-input" value={terminationDate} onChange={(e) => setTerminationDate(e.target.value)} disabled={busy} />
          <span className="help">Boş bırakılırsa yalnız durum değişir.</span>
        </div>
      ) : null}

      <div className="form-field">
        <span className="lbl">Pozisyon</span>
        <SearchSelect value={position} options={posOpts} onChange={(v) => setPosition(v ?? KEEP)} placeholder="Değiştirme" searchPlaceholder="Pozisyon ara…" disabled={busy} clearable={false} />
      </div>

      {orgUnitOptions.length > 0 ? (
        <div className="form-field">
          <span className="lbl">Birim</span>
          <SearchSelect value={orgUnit} options={unitOpts} onChange={(v) => setOrgUnit(v ?? KEEP)} placeholder="Değiştirme" searchPlaceholder="Birim ara…" disabled={busy} clearable={false} />
        </div>
      ) : null}

      <div className="form-field">
        <label className="lbl" htmlFor="bulk-emptype">Çalışma Şekli</label>
        <select id="bulk-emptype" className="form-select" value={empType} onChange={(e) => setEmpType(e.target.value as EmpType | "")} disabled={busy}>
          <option value="">Değiştirme</option>
          {(Object.keys(EMP_TYPE_LABEL) as EmpType[]).map((t) => <option key={t} value={t}>{EMP_TYPE_LABEL[t]}</option>)}
        </select>
      </div>
    </Drawer>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Detay Drawer — Özlük + Görev geçmişi + Evrak + Maaş
// ════════════════════════════════════════════════════════════════════════════

/**
 * PERSONEL KARTI SİHİRBAZI — sol raydaki bölümler (sevkiyat drawer'ındaki desenin İK varyantı).
 *
 * `req(d)` = o adımda EKSİK zorunlu alanların etiketleri → rayda ⚠. Sevkiyattan bilinçli SAPMA:
 * orada req() SUNUCU verisini okur (birincil aksiyon "Onayla" kaydedilmişe bakar); burada birincil
 * aksiyon KAYDIN KENDİSİ ve mevcut kapı (canSave) zaten taslağı okuyor → req de taslağı okur,
 * aksi halde kullanıcı adı yazdıktan sonra da uyarı inatla kalırdı.
 * Zorunluluk listesi SUNUCUDAN türetildi: create yalnız firstName/lastName ister; kalan alanlar
 * opsiyoneldir → uydurma zorunlu alan EKLENMEZ.
 * `avail` = adım şu an gezilebilir mi (yeni personelde alt kayıt uçları employeeId ister).
 */
const WIZ_STEPS: Array<{
  key: string;
  label: string;
  icon: string;
  req: (d: Draft) => string[];
  avail: (c: { isEdit: boolean }) => boolean;
  vis?: (c: { isSuper: boolean }) => boolean;
}> = [
  {
    key: "kimlik", label: "Kimlik & Özlük", icon: "user", avail: () => true,
    req: (d) => [...(d.firstName.trim() ? [] : ["Ad"]), ...(d.lastName.trim() ? [] : ["Soyad"])],
  },
  { key: "iletisim", label: "İletişim & Acil Durum", icon: "mapPin", avail: () => true, req: () => [] },
  { key: "kadro", label: "Kadro & Görev", icon: "building", avail: () => true, req: () => [] },
  { key: "gorev", label: "Görev Geçmişi", icon: "clock", avail: (c) => c.isEdit, req: () => [] },
  { key: "evrak", label: "Evraklar", icon: "fileText", avail: (c) => c.isEdit, req: () => [] },
  { key: "maas", label: "Maaş & Ücret", icon: "banknote", avail: (c) => c.isEdit, req: () => [], vis: (c) => c.isSuper },
];

interface Draft {
  firstName: string; lastName: string; tc: string; iban: string;
  birthDate: string; gender: Gender; maritalStatus: Marital;
  phone: string; personalEmail: string; address: string;
  emergencyContact: string; emergencyPhone: string;
  positionId: string | null; orgUnitId: string | null; managerId: string | null;
  employmentType: EmpType; status: Status; hireDate: string;
}
const emptyDraft = (): Draft => ({
  firstName: "", lastName: "", tc: "", iban: "",
  birthDate: "", gender: "unspecified", maritalStatus: "unspecified",
  phone: "", personalEmail: "", address: "",
  emergencyContact: "", emergencyPhone: "",
  positionId: null, orgUnitId: null, managerId: null,
  employmentType: "full_time", status: "active", hireDate: "",
});

function EmployeeDetail({
  employeeId, canManage, isSuper, onClose, onCreated, onChanged, pushToast,
}: {
  employeeId: string | null;
  canManage: boolean;
  isSuper: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  onChanged: () => void;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const qc = trpc.useUtils();
  const isEdit = employeeId != null;
  const ro = !canManage; // salt-okunur (view:hr ama manage:hr değil)

  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [err, setErr] = useState<string | null>(null);
  const [delOpen, setDelOpen] = useState(false);
  const initedRef = useRef(false);

  const getQ = trpc.hr.employee.get.useQuery({ id: employeeId ?? "" }, { enabled: isEdit, staleTime: 3_000 });
  const emp = getQ.data;

  // Seçici kaynakları (drawer açıkken). orgUnit view:org ister → yetki yoksa sessizce boş.
  const pickEmpQ = trpc.hr.employee.list.useQuery(undefined, { staleTime: 30_000 });
  const positionsQ = trpc.hr.position.list.useQuery(undefined, { staleTime: 30_000 });
  const orgUnitsQ = trpc.hr.orgUnit.list.useQuery(undefined, { staleTime: 30_000, retry: false });

  useEffect(() => {
    if (isEdit && emp && !initedRef.current) {
      setDraft({
        firstName: emp.firstName ?? "", lastName: emp.lastName ?? "",
        tc: emp.tc ?? "", iban: emp.iban ?? "",
        birthDate: toDateInput(emp.birthDate), gender: (emp.gender as Gender) ?? "unspecified",
        maritalStatus: (emp.maritalStatus as Marital) ?? "unspecified",
        phone: emp.phone ?? "", personalEmail: emp.personalEmail ?? "", address: emp.address ?? "",
        emergencyContact: emp.emergencyContact ?? "", emergencyPhone: emp.emergencyPhone ?? "",
        positionId: emp.positionId ?? null, orgUnitId: emp.orgUnitId ?? null, managerId: emp.managerId ?? null,
        employmentType: (emp.employmentType as EmpType) ?? "full_time",
        status: (emp.status as Status) ?? "active", hireDate: toDateInput(emp.hireDate),
      });
      initedRef.current = true;
    }
  }, [isEdit, emp]);

  const positionOptions = useMemo<SSOption[]>(
    () => (positionsQ.data ?? []).map((p) => ({ value: p.id, label: p.title, desc: p.orgUnitName ?? undefined, tail: p.code ?? undefined, icon: "user" })),
    [positionsQ.data],
  );
  const orgUnitOptions = useMemo<SSOption[]>(
    () => (orgUnitsQ.data ?? []).map((u) => ({ value: u.id, label: u.name, desc: u.tesisName ?? undefined, icon: "building" })),
    [orgUnitsQ.data],
  );
  const managerOptions = useMemo<SSOption[]>(
    () => (pickEmpQ.data ?? [])
      .filter((e) => e.id !== employeeId)
      .map((e) => ({ value: e.id, label: e.fullName, desc: e.positionTitle ?? undefined, tail: e.code ?? undefined, icon: "user" })),
    [pickEmpQ.data, employeeId],
  );

  const setF = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const createMut = trpc.hr.employee.create.useMutation();
  const updateMut = trpc.hr.employee.update.useMutation();
  const removeMut = trpc.hr.employee.remove.useMutation();
  const busy = createMut.isPending || updateMut.isPending;
  const canSave = !ro && draft.firstName.trim().length > 0 && draft.lastName.trim().length > 0;

  // ── Sihirbaz durumu ──
  const [step, setStep] = useState<string>(WIZ_STEPS[0]!.key);
  const [tried, setTried] = useState(false); // Kaydet'e basıldı mı (erken kırmızı uyarı olmasın)
  const [visited, setVisited] = useState<Set<string>>(() => new Set([WIZ_STEPS[0]!.key]));
  const railRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const visSteps = useMemo(() => WIZ_STEPS.filter((s) => (s.vis ? s.vis({ isSuper }) : true)), [isSuper]);
  // Gezinme YALNIZ erişilebilir adımlar üzerinde → İleri/Geri asla kilitli adıma düşmez.
  const navSteps = useMemo(() => visSteps.filter((s) => s.avail({ isEdit })), [visSteps, isEdit]);
  const locked = visSteps.length > navSteps.length;
  // İKİ AYRI ŞEY: (1) zorunluluk İŞARETİ (* ve raydaki ⚠) düzenleyen kullanıcıya HER ZAMAN
  // gösterilir — hangi alanın zorunlu olduğunu baştan bilmeli. (2) kırmızı HATA BANDI
  // (role="alert") yalnız kayıtlı kartta ya da bir kez Kaydet denendikten sonra çıkar; yeni kart
  // açılır açılmaz hata duyurmak, kullanıcının yapmadığı bir hatayı bildirmek olurdu.
  const showReq = !ro;
  const showErrBanner = showReq && (isEdit || tried);
  const stepMissing = useMemo<Record<string, string[]>>(() => {
    const out: Record<string, string[]> = {};
    for (const st of visSteps) out[st.key] = showReq ? st.req(draft) : [];
    return out;
  }, [draft, showReq, visSteps]);
  // Görünmeyen adımın eksiği banner'a yazılmaz (kullanıcı ona ulaşamaz).
  const missingAll = useMemo(() => visSteps.flatMap((st) => stepMissing[st.key] ?? []), [stepMissing, visSteps]);
  const stepIdx = Math.max(0, navSteps.findIndex((st) => st.key === step));
  const goStep = (key: string) => { setStep(key); setVisited((p) => (p.has(key) ? p : new Set(p).add(key))); };
  // Erişilemez bir adımda kalmayalım (kayıt sonrası/rol değişiminde).
  useEffect(() => {
    if (navSteps.length > 0 && !navSteps.some((s) => s.key === step)) setStep(navSteps[0]!.key);
  }, [navSteps, step]);
  // Adım değişince: içerik paneline odaklan (ekran okuyucu değişimi duysun) + mobil yatay rayda
  // aktif sekmeyi görünür kıl (aksi halde 3. adımdan sonra aktif çip ekran dışında kalıyor).
  useEffect(() => {
    railRef.current?.querySelector<HTMLElement>(".emp-wiz-step.act")?.scrollIntoView({ inline: "nearest", block: "nearest" });
    panelRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  /** Sekme rayında ok tuşlarıyla gezinme (roving tabindex ile birlikte tam ARIA tablist davranışı). */
  function onRailKey(e: React.KeyboardEvent<HTMLElement>, idx: number) {
    const keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const n = visSteps.length;
    const next =
      e.key === "Home" ? 0 : e.key === "End" ? n - 1
      : e.key === "ArrowRight" || e.key === "ArrowDown" ? (idx + 1) % n : (idx - 1 + n) % n;
    const target = visSteps[next]!;
    if (target.avail({ isEdit })) goStep(target.key);
    railRef.current?.querySelectorAll<HTMLElement>(".emp-wiz-step")[next]?.focus();
  }

  function payload() {
    return {
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      tc: draft.tc.trim() || null,
      iban: draft.iban.trim() || null,
      birthDate: draft.birthDate || null,
      gender: draft.gender,
      maritalStatus: draft.maritalStatus,
      phone: draft.phone.trim() || null,
      personalEmail: draft.personalEmail.trim() || null,
      address: draft.address.trim() || null,
      emergencyContact: draft.emergencyContact.trim() || null,
      emergencyPhone: draft.emergencyPhone.trim() || null,
      positionId: draft.positionId,
      orgUnitId: draft.orgUnitId,
      managerId: draft.managerId,
      employmentType: draft.employmentType,
      status: draft.status,
      hireDate: draft.hireDate || null,
    };
  }

  async function submit() {
    setTried(true);
    if (!canSave) { setErr("Ad ve soyad zorunlu."); return; }
    setErr(null);
    try {
      if (isEdit && employeeId) {
        await updateMut.mutateAsync({ id: employeeId, ...payload() });
        await qc.hr.employee.get.invalidate({ id: employeeId });
        onChanged();
        pushToast({ tone: "success", message: "Personel güncellendi." });
      } else {
        const res = await createMut.mutateAsync(payload());
        onChanged();
        pushToast({ tone: "success", message: "Personel oluşturuldu." });
        onCreated(res.id); // düzenleme moduna geç → evrak/maaş bölümleri açılır
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const title = isEdit ? (emp ? `${emp.code} — ${emp.fullName}` : "Personel") : "Yeni Personel";
  const subtitle = isEdit ? "Özlük · evrak · görev geçmişi" + (isSuper ? " · maaş" : "") : "Kod otomatik üretilir (PER-yıl-sıra)";

  return (
    <>
      <Drawer
        open onClose={onClose} size="3xl" dismissible={false}
        title={title} subtitle={subtitle}
        footer={
          <div className="emp-wiz-foot">
            <button
              type="button" className="btn-md secondary" disabled={stepIdx === 0}
              onClick={() => goStep(navSteps[Math.max(0, stepIdx - 1)]!.key)}
            >
              <Icon name="arrowLeft" size={15} />Geri
            </button>
            <span className="emp-wiz-pos mono" aria-live="polite">
              {stepIdx + 1} / {navSteps.length}{locked ? " açık" : ""} · {navSteps[stepIdx]?.label}
            </span>
            <button
              type="button" className="btn-md secondary" disabled={stepIdx >= navSteps.length - 1}
              onClick={() => goStep(navSteps[Math.min(navSteps.length - 1, stepIdx + 1)]!.key)}
            >
              İleri<Icon name="arrowRight" size={15} />
            </button>
          </div>
        }
      >
        {/* Kayıt aksiyonları adımdan BAĞIMSIZ + YAPIŞKAN: footer gezinmeye ayrıldı, bu şerit her
            adımda ve uzun listelerde kaydırırken de görünür kalır. Yükleme/hata durumunda da
            render edilir — aksi halde hata ekranında "Kapat" hiç görünmüyordu. */}
        <div className="emp-actions">
          {!ro && !(isEdit && (getQ.isLoading || getQ.isError)) ? (
            <button type="button" className="btn-md primary" onClick={submit} disabled={!canSave || busy}>
              <Icon name="check" size={14} />{busy ? "Kaydediliyor…" : isEdit ? "Kaydet" : "Oluştur"}
            </button>
          ) : null}
          {isEdit && !ro && !getQ.isLoading && !getQ.isError ? (
            <button type="button" className="btn-md danger-outline" onClick={() => { setErr(null); setDelOpen(true); }}>
              <Icon name="trash" size={14} />Sil
            </button>
          ) : null}
          <button type="button" className="btn-md ghost emp-act-end" onClick={onClose}>Kapat</button>
        </div>

        {err ? <div className="form-banner-err">{err}</div> : null}

        {isEdit && getQ.isLoading ? (
          <div className="mt-empty">Yükleniyor…</div>
        ) : isEdit && getQ.isError ? (
          <div className="form-banner-err">Personel yüklenemedi: {getQ.error.message}</div>
        ) : (
          <>
            {showErrBanner && missingAll.length > 0 ? (
              <div className="form-banner-err" role="alert">
                <Icon name="alertTriangle" size={14} />
                <span>Kayıt için eksik zorunlu alan{missingAll.length > 1 ? "lar" : ""}: <b>{missingAll.join(" · ")}</b></span>
              </div>
            ) : null}

            <div className="emp-wiz">
              <nav className="emp-wiz-nav" ref={railRef} role="tablist" aria-orientation="vertical" aria-label="Personel kartı bölümleri">
                {visSteps.map((st, i) => {
                  const miss = stepMissing[st.key] ?? [];
                  const act = st.key === step;
                  const ok = st.avail({ isEdit });
                  return (
                    <button
                      key={st.key}
                      type="button"
                      role="tab"
                      id={`emp-tab-${st.key}`}
                      aria-controls="emp-wiz-panel"
                      aria-selected={act}
                      // `disabled` YERİNE `aria-disabled`: devre dışı butona tarayıcı pointer olayı
                      // göndermediği için `title` ipucu hiç görünmüyordu (kilit gerekçesi kayboluyordu).
                      aria-disabled={!ok || undefined}
                      tabIndex={act ? 0 : -1}
                      className={`emp-wiz-step${act ? " act" : ""}${miss.length ? " miss" : ""}${ok ? "" : " off"}`}
                      title={!ok ? "Personel kaydedildikten sonra açılır" : miss.length ? `Eksik zorunlu alan: ${miss.join(", ")}` : st.label}
                      onKeyDown={(e) => onRailKey(e, i)}
                      onClick={() => { if (ok) goStep(st.key); }}
                    >
                      <span className="ic"><Icon name={st.icon} size={15} /></span>
                      <span className="t">{st.label}</span>
                      {miss.length ? (
                        <span className="w" role="img" aria-label={`${miss.length} eksik zorunlu alan: ${miss.join(", ")}`}>
                          <Icon name="alertTriangle" size={14} /><b>{miss.length}</b>
                        </span>
                      ) : !ok ? (
                        <span className="lock" role="img" aria-label="Personel kaydedildikten sonra açılır">
                          <Icon name="clock" size={13} />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </nav>

              <div
                className="emp-wiz-main" id="emp-wiz-panel" ref={panelRef}
                role="tabpanel" tabIndex={-1} aria-labelledby={`emp-tab-${step}`}
              >
                {locked ? (
                  <p className="emp-locknote">
                    <Icon name="clock" size={13} />
                    Görev geçmişi, evrak ve maaş bölümleri personel kaydedildikten sonra açılır.
                  </p>
                ) : null}
                <div className="emp-sections">
            {/* ── (1) Kimlik & Özlük ── */}
            <section className={`d-card${step === "kimlik" ? "" : " emp-hide"}`}>
              <div className="d-card-head"><div className="left"><span className="t">Kimlik & Özlük</span></div></div>
              <div className="d-card-body">
                <div className="form-row">
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-fn">Ad {showReq ? <span className="req">*</span> : null}</label>
                    <input id="emp-fn" className="form-input" value={draft.firstName} disabled={ro} onChange={(e) => setF("firstName", e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-ln">Soyad {showReq ? <span className="req">*</span> : null}</label>
                    <input id="emp-ln" className="form-input" value={draft.lastName} disabled={ro} onChange={(e) => setF("lastName", e.target.value)} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-tc">T.C. Kimlik No</label>
                    <input id="emp-tc" className="form-input mono" value={draft.tc} disabled={ro} onChange={(e) => setF("tc", e.target.value)} inputMode="numeric" maxLength={20} />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-iban">IBAN</label>
                    <input id="emp-iban" className="form-input mono" value={draft.iban} disabled={ro} onChange={(e) => setF("iban", e.target.value)} maxLength={40} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-bd">Doğum Tarihi</label>
                    <input id="emp-bd" className="form-input" type="date" value={draft.birthDate} disabled={ro} onChange={(e) => setF("birthDate", e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-gender">Cinsiyet</label>
                    <select id="emp-gender" className="form-select" value={draft.gender} disabled={ro} onChange={(e) => setF("gender", e.target.value as Gender)}>
                      {(Object.keys(GENDER_LABEL) as Gender[]).map((g) => <option key={g} value={g}>{GENDER_LABEL[g]}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-field">
                  <label className="lbl" htmlFor="emp-ms">Medeni Durum</label>
                  <select id="emp-ms" className="form-select" value={draft.maritalStatus} disabled={ro} onChange={(e) => setF("maritalStatus", e.target.value as Marital)}>
                    {(Object.keys(MARITAL_LABEL) as Marital[]).map((m) => <option key={m} value={m}>{MARITAL_LABEL[m]}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* ── (2) İletişim & Acil Durum ── */}
            <section className={`d-card${step === "iletisim" ? "" : " emp-hide"}`}>
              <div className="d-card-head"><div className="left"><span className="t">İletişim & Acil Durum</span></div></div>
              <div className="d-card-body">
                <div className="form-row">
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-phone">Telefon</label>
                    <input id="emp-phone" className="form-input" value={draft.phone} disabled={ro} onChange={(e) => setF("phone", e.target.value)} maxLength={40} />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-email">Kişisel E-posta</label>
                    <input id="emp-email" className="form-input" type="email" value={draft.personalEmail} disabled={ro} onChange={(e) => setF("personalEmail", e.target.value)} maxLength={160} />
                  </div>
                </div>

                <div className="form-field">
                  <label className="lbl" htmlFor="emp-addr">Adres</label>
                  <textarea id="emp-addr" className="form-textarea" rows={2} value={draft.address} disabled={ro} onChange={(e) => setF("address", e.target.value)} maxLength={500} />
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-ec">Acil Durumda Aranacak</label>
                    <input id="emp-ec" className="form-input" value={draft.emergencyContact} disabled={ro} onChange={(e) => setF("emergencyContact", e.target.value)} maxLength={160} />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-ep">Acil Durum Telefonu</label>
                    <input id="emp-ep" className="form-input" value={draft.emergencyPhone} disabled={ro} onChange={(e) => setF("emergencyPhone", e.target.value)} maxLength={40} />
                  </div>
                </div>
              </div>
            </section>

            {/* ── (3) Kadro & Görev ── */}
            <section className={`d-card${step === "kadro" ? "" : " emp-hide"}`}>
              <div className="d-card-head"><div className="left"><span className="t">Kadro & Görev</span></div></div>
              <div className="d-card-body">
                <div className="form-row">
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-pos">Pozisyon</label>
                    <SearchSelect id="emp-pos" value={draft.positionId} options={positionOptions} disabled={ro}
                      onChange={(v) => setF("positionId", v)} placeholder="Pozisyon seç…" searchPlaceholder="Pozisyon ara…"
                      emptyText="Pozisyon yok" icon="user" />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-ou">Birim</label>
                    <SearchSelect id="emp-ou" value={draft.orgUnitId} options={orgUnitOptions} disabled={ro}
                      onChange={(v) => setF("orgUnitId", v)} placeholder="Birim seç…" searchPlaceholder="Birim ara…"
                      emptyText="Birim yok" icon="building" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-mgr">Yönetici</label>
                    <SearchSelect id="emp-mgr" value={draft.managerId} options={managerOptions} disabled={ro}
                      onChange={(v) => setF("managerId", v)} placeholder="Yönetici seç…" searchPlaceholder="Personel ara…"
                      emptyText="Personel yok" icon="user" />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-et">Çalışma Türü</label>
                    <select id="emp-et" className="form-select" value={draft.employmentType} disabled={ro} onChange={(e) => setF("employmentType", e.target.value as EmpType)}>
                      {(Object.keys(EMP_TYPE_LABEL) as EmpType[]).map((t) => <option key={t} value={t}>{EMP_TYPE_LABEL[t]}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-status">Durum</label>
                    <select id="emp-status" className="form-select" value={draft.status} disabled={ro} onChange={(e) => setF("status", e.target.value as Status)}>
                      {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="emp-hd">İşe Giriş Tarihi</label>
                    <input id="emp-hd" className="form-input" type="date" value={draft.hireDate} disabled={ro} onChange={(e) => setF("hireDate", e.target.value)} />
                  </div>
                </div>
              </div>
            </section>

            {/* ── (4) Görev Geçmişi · (5) Evraklar · (6) Maaş — yalnız kayıtlı personelde.
                   İLK ZİYARETTE mount edilir (maaş/evrak sorguları hiç açılmayan adım için koşmasın),
                   sonrasında `emp-hide` ile gizlenir ama UNMOUNT EDİLMEZ: alt bölümlerin kendi form
                   state'i (yeni atama/evrak/maaş satırı) adım değişiminde uçmasın. */}
            {isEdit && employeeId && visited.has("gorev") ? (
              <div className={step === "gorev" ? undefined : "emp-hide"}>
                <AssignmentsSection
                  employeeId={employeeId}
                  assignments={emp?.assignments ?? []}
                  positionOptions={positionOptions}
                  orgUnitOptions={orgUnitOptions}
                  canManage={canManage}
                  onChanged={() => qc.hr.employee.get.invalidate({ id: employeeId })}
                  pushToast={pushToast}
                />
              </div>
            ) : null}

            {isEdit && employeeId && visited.has("evrak") ? (
              <div className={step === "evrak" ? undefined : "emp-hide"}>
                <DocumentsSection employeeId={employeeId} canManage={canManage} pushToast={pushToast} />
              </div>
            ) : null}

            {isEdit && employeeId && isSuper && visited.has("maas") ? (
              <div className={step === "maas" ? undefined : "emp-hide"}>
                <SalarySection employeeId={employeeId} pushToast={pushToast} />
              </div>
            ) : null}
                </div>
              </div>
            </div>
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={delOpen}
        title="Personel silinsin mi?"
        message={emp ? `"${emp.fullName}" personeli ve bağlı kayıtları (izin, puantaj, maaş, evrak, görev) kalıcı olarak silinecek. Genelde durumu "Ayrıldı" yapmak tercih edilir.` : ""}
        tone="danger" confirmLabel="Kalıcı Olarak Sil" cancelLabel="Vazgeç"
        busy={removeMut.isPending}
        onCancel={() => setDelOpen(false)}
        onConfirm={() => {
          if (!employeeId) return;
          removeMut.mutate({ id: employeeId }, {
            onSuccess: () => { setDelOpen(false); onChanged(); pushToast({ tone: "success", message: "Personel silindi." }); onClose(); },
            onError: (e) => { setDelOpen(false); setErr(e.message); },
          });
        }}
      />
    </>
  );
}

// ── Görev geçmişi (assignments) ──────────────────────────────────────────────

function AssignmentsSection({
  employeeId, assignments, positionOptions, orgUnitOptions, canManage, onChanged, pushToast,
}: {
  employeeId: string;
  assignments: { id: string; positionTitle: string | null; startDate: string | Date | null; endDate: string | Date | null; note: string | null }[];
  positionOptions: SSOption[];
  orgUnitOptions: SSOption[];
  canManage: boolean;
  onChanged: () => void;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const [positionId, setPositionId] = useState<string | null>(null);
  const [orgUnitId, setOrgUnitId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState("");
  const [note, setNote] = useState("");

  const addMut = trpc.hr.employee.addAssignment.useMutation({
    onSuccess: () => {
      setPositionId(null); setOrgUnitId(null); setStartDate(""); setNote("");
      onChanged();
      pushToast({ tone: "success", message: "Görev ataması eklendi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Eklenemedi", message: e.message }),
  });
  const canAdd = canManage && !!positionId && !!startDate && !addMut.isPending;

  return (
    <section className="d-card">
      <div className="d-card-head"><div className="left"><span className="t">Görev Geçmişi</span><span className="s">{assignments.length} atama</span></div></div>
      <div className="d-card-body flush">
        {assignments.length === 0 ? (
          <div className="mt-empty">Henüz görev ataması yok.</div>
        ) : (
          <table className="dt2">
            <thead><tr><th>Pozisyon</th><th className="emp-w-date">Başlangıç</th><th className="emp-w-date">Bitiş</th><th>Not</th></tr></thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id}>
                  <td>{a.positionTitle ?? "—"}</td>
                  <td className="mono">{fmtDate(a.startDate)}</td>
                  <td className="mono">{a.endDate ? fmtDate(a.endDate) : <span className="emp-muted">Devam ediyor</span>}</td>
                  <td className="emp-note">{a.note ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {canManage ? (
        <div className="d-card-body emp-sub">
          <div className="form-row">
            <div className="form-field">
              <span className="lbl">Pozisyon <span className="req">*</span></span>
              <SearchSelect value={positionId} options={positionOptions} onChange={setPositionId}
                placeholder="Pozisyon seç…" searchPlaceholder="Pozisyon ara…" emptyText="Pozisyon yok" icon="user" />
            </div>
            <div className="form-field">
              <span className="lbl">Birim</span>
              <SearchSelect value={orgUnitId} options={orgUnitOptions} onChange={setOrgUnitId}
                placeholder="Birim seç…" searchPlaceholder="Birim ara…" emptyText="Birim yok" icon="building" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor="asg-start">Başlangıç <span className="req">*</span></label>
              <input id="asg-start" className="form-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="asg-note">Not</label>
              <input id="asg-note" className="form-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
            </div>
          </div>
          <button type="button" className="btn-md secondary" disabled={!canAdd}
            onClick={() => positionId && addMut.mutate({ employeeId, positionId, orgUnitId: orgUnitId ?? null, startDate, note: note.trim() || null })}>
            <Icon name="plus" size={14} />{addMut.isPending ? "Ekleniyor…" : "Görev Ataması Ekle"}
          </button>
        </div>
      ) : null}
    </section>
  );
}

// ── Evrak bölümü ──────────────────────────────────────────────────────────────

function DocumentsSection({
  employeeId, canManage, pushToast,
}: {
  employeeId: string;
  canManage: boolean;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const qc = trpc.useUtils();
  const listQ = trpc.hr.document.list.useQuery({ employeeId }, { staleTime: 5_000 });
  const docs = listQ.data ?? [];

  const [category, setCategory] = useState<DocCategory>("other");
  const [docTitle, setDocTitle] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const invalidate = () => qc.hr.document.list.invalidate({ employeeId });
  const uploadMut = trpc.hr.document.upload.useMutation({
    onSuccess: () => { invalidate(); setDocTitle(""); setIssuedAt(""); setExpiresAt(""); setCategory("other"); pushToast({ tone: "success", message: "Evrak yüklendi." }); },
    onError: (e) => setErr(e.message),
  });
  const removeMut = trpc.hr.document.remove.useMutation({
    onSuccess: () => { invalidate(); pushToast({ tone: "success", message: "Evrak silindi." }); },
    onError: (e) => pushToast({ tone: "danger", title: "Silinemedi", message: e.message }),
  });

  const canUpload = canManage && docTitle.trim().length > 0 && !uploadMut.isPending;

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null);
    const reader = new FileReader();
    reader.onload = () => {
      uploadMut.mutate({
        employeeId,
        category,
        title: docTitle.trim(),
        filename: file.name,
        dataBase64: String(reader.result),
        issuedAt: issuedAt || null,
        expiresAt: expiresAt || null,
      });
    };
    reader.readAsDataURL(file);
  }

  return (
    <section className="d-card">
      <div className="d-card-head"><div className="left"><span className="t">Evraklar</span><span className="s">{docs.length} belge</span></div></div>
      <div className="d-card-body">
        {err ? <div className="form-banner-err">{err}</div> : null}

        {listQ.isLoading ? (
          <div className="mt-empty">Yükleniyor…</div>
        ) : docs.length === 0 ? (
          <div className="mt-empty">
            Henüz evrak yok{canManage ? " — aşağıdan yükleyebilirsiniz." : "."}
          </div>
        ) : (
          <div className="emp-doc-grid">
            {docs.map((d) => {
              const exp = expiryBadge(d.expiresAt);
              return (
                <div key={d.id} className="emp-doc-it">
                  <DocChip
                    url={d.url}
                    isPdf={d.kind === "document"}
                    label={DOC_CATEGORY_LABEL[d.category as DocCategory] ?? d.category}
                    onOpen={setPreviewUrl}
                    onRemove={canManage ? () => removeMut.mutate({ id: d.id }) : undefined}
                    busy={removeMut.isPending}
                  />
                  <span className="nm" title={d.title}>{d.title}</span>
                  {exp ? <span className={`badge-pill ${exp.cls}`}><Icon name={exp.cls === "b-danger" ? "alertTriangle" : "calendar"} size={10} />{exp.label}</span> : null}
                </div>
              );
            })}
          </div>
        )}

        {canManage ? (
          <div className="emp-subform">
            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="doc-title">Belge Başlığı <span className="req">*</span></label>
                <input id="doc-title" className="form-input" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="Örn. İş Sözleşmesi" maxLength={200} />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="doc-cat">Kategori</label>
                <select id="doc-cat" className="form-select" value={category} onChange={(e) => setCategory(e.target.value as DocCategory)}>
                  {(Object.keys(DOC_CATEGORY_LABEL) as DocCategory[]).map((c) => <option key={c} value={c}>{DOC_CATEGORY_LABEL[c]}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="doc-issued">Düzenlenme Tarihi</label>
                <input id="doc-issued" className="form-input" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="doc-expires">Son Kullanma (SKT)</label>
                <input id="doc-expires" className="form-input" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" className="emp-file-native" onChange={onPick} />
            <button type="button" className="btn-md secondary" disabled={!canUpload} onClick={() => fileRef.current?.click()}>
              <Icon name="upload" size={14} />{uploadMut.isPending ? "Yükleniyor…" : "Belge Yükle (Foto / PDF)"}
            </button>
            {docTitle.trim().length === 0 ? <span className="emp-hint">Önce belge başlığı girin.</span> : null}
          </div>
        ) : null}
      </div>

      <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
    </section>
  );
}

// ── Maaş bölümü (yalnız süper-admin) ─────────────────────────────────────────

function SalarySection({
  employeeId, pushToast,
}: {
  employeeId: string;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const qc = trpc.useUtils();
  const listQ = trpc.hr.salary.list.useQuery({ employeeId }, { staleTime: 5_000 });
  const records = listQ.data ?? [];

  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<Currency>("TRY");
  const [type, setType] = useState<SalaryType>("base");
  const [effectiveFrom, setEffectiveFrom] = useState("");

  const addMut = trpc.hr.salary.create.useMutation({
    onSuccess: () => {
      qc.hr.salary.list.invalidate({ employeeId });
      setAmount(""); setEffectiveFrom(""); setType("base"); setCurrency("TRY");
      pushToast({ tone: "success", message: "Maaş kaydı eklendi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Eklenemedi", message: e.message }),
  });

  // TR sayı formatı: "12.500" = 12500, "1.234,56" = 1234.56 (kanonik parseTrNumber — finansal yol).
  // Naif Number(replace) "12.500"i 12.5 sanardı → maaş ~1000 kat yanlış, bordro bozulurdu.
  const amountNum = parseTrNumber(amount);
  const canAdd = amount.trim().length > 0 && !Number.isNaN(amountNum) && amountNum >= 0 && !!effectiveFrom && !addMut.isPending;

  return (
    <section className="d-card">
      <div className="d-card-head"><div className="left"><span className="t">Maaş & Ücret</span><span className="s">yalnız süper-admin</span></div></div>
      <div className="d-card-body flush">
        {listQ.isLoading ? (
          <div className="mt-empty">Yükleniyor…</div>
        ) : records.length === 0 ? (
          <div className="mt-empty">Henüz maaş kaydı yok.</div>
        ) : (
          <table className="dt2">
            <thead>
              <tr>
                <th className="num emp-w-money">Tutar</th>
                <th className="emp-w-type">Tür</th>
                <th className="emp-w-date">Geçerli</th>
                <th className="emp-w-date">Bitiş</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id}>
                  <td className="cell-num">{fmtMoney(r.amount)} <span className="unit">{r.currency}</span></td>
                  <td><span className={`badge-pill ${r.type === "deduction" ? "b-danger" : r.type === "base" ? "b-primary" : r.type === "overtime_rate" ? "b-warn" : "b-info"}`}>{SALARY_TYPE_LABEL[r.type as SalaryType] ?? r.type}</span></td>
                  <td className="mono">{fmtDate(r.effectiveFrom)}</td>
                  <td className="mono">{r.effectiveTo ? fmtDate(r.effectiveTo) : <span className="emp-muted">Açık</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="d-card-body emp-sub">
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="sal-amount">Tutar <span className="req">*</span></label>
            <input id="sal-amount" className="form-input mono" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="sal-cur">Para Birimi</label>
            <select id="sal-cur" className="form-select" value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
              {(["TRY", "USD", "EUR", "GBP"] as Currency[]).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="sal-type">Tür</label>
            <select id="sal-type" className="form-select" value={type} onChange={(e) => setType(e.target.value as SalaryType)}>
              {(Object.keys(SALARY_TYPE_LABEL) as SalaryType[]).map((t) => <option key={t} value={t}>{SALARY_TYPE_LABEL[t]}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="sal-from">Geçerlilik Başlangıcı <span className="req">*</span></label>
            <input id="sal-from" className="form-input" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </div>
        </div>
        <button type="button" className="btn-md secondary" disabled={!canAdd}
          onClick={() => addMut.mutate({ employeeId, amount: amountNum, currency, type, effectiveFrom })}>
          <Icon name="plus" size={14} />{addMut.isPending ? "Ekleniyor…" : "Maaş Kaydı Ekle"}
        </button>
        {type === "base" ? <span className="emp-hint">Temel ücret eklenince önceki açık temel ücret otomatik kapatılır.</span> : null}
        {type === "overtime_rate" ? (
          <span className="emp-hint">
            AYLIK değil, <b>bir saatlik fazla mesainin ücreti</b>. Girilirse bu kişide pozisyonun oranı
            yerine bu tutar kullanılır ve bordro katsayısı (1,5) uygulanmaz. Önceki açık oran otomatik kapatılır.
          </span>
        ) : null}
      </div>
    </section>
  );
}
